import { partfinitiClient } from 'core/services';
import {
  getQueryString,
  objectKeysCamelToSnakeCase,
  objectKeysSnakeCaseToCamelCase,
} from 'core/utils';
import {
  IPartFieldConfig,
  IPartFieldConfigs,
  IPartFieldConfigsRequestPayload,
  IPartSpecifications,
  IPartSpecificationsRequestPayload,
  ISavePartParticularsPayload,
  ISavePartParticularsResponse,
} from 'parts/types';

export const getPartSpecifications = async (
  requestPayload: IPartSpecificationsRequestPayload,
  signal?: AbortSignal
): Promise<IPartSpecifications> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/specifications/data?${getQueryString(objectKeysCamelToSnakeCase(requestPayload))}`,
    signal
  );

  const camelCased = objectKeysSnakeCaseToCamelCase(data);

  return {
    partFieldValues: camelCased?.partFieldValues ?? [],
    imagesData: camelCased?.imagesData ?? [],
  };
};

export const getPartFieldConfigs = async (
  requestPayload: IPartFieldConfigsRequestPayload,
  signal?: AbortSignal
): Promise<IPartFieldConfigs> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/specifications/get-part-field-configs?${getQueryString(
      objectKeysCamelToSnakeCase(requestPayload)
    )}`,
    signal
  );

  const camelCased = objectKeysSnakeCaseToCamelCase(data);

  // BE response: `{ partFieldConfigs: { columns: [[...], [...]] } }` — the BE has already
  // balanced fields across two columns matching the legacy modal layout. Preserve that split
  // for the edit drawer's two-column responsive form (FormGroupWrapper per column).
  const rawColumns = camelCased?.partFieldConfigs?.columns;

  const isHidden = (config?: IPartFieldConfig): boolean => {
    const hidden = config?.config?.hidden;

    return hidden === '1' || hidden === 1;
  };

  let partFieldConfigColumns: IPartFieldConfig[][] = [];

  if (Array.isArray(rawColumns)) {
    partFieldConfigColumns = rawColumns.map((column) =>
      (Array.isArray(column) ? column : []).filter((config) => !isHidden(config))
    );
  } else if (Array.isArray(camelCased?.partFieldConfigs)) {
    // Legacy / unwrapped shape — degrade to a single column rather than crashing.
    partFieldConfigColumns = [camelCased.partFieldConfigs.filter((config) => !isHidden(config))];
  }

  return { partFieldConfigColumns };
};

export const savePartParticulars = async (
  payload: ISavePartParticularsPayload
): Promise<ISavePartParticularsResponse> => {
  const response: ISavePartParticularsResponse = await partfinitiClient.postForm(
    '/parts/edit/save-particulars',
    payload
  );

  // BE returns `errors: [{ field, message }]` on validation failure. `useHandleErrors` would
  // render the array as JSX children and crash; flatten to a single `error` string instead.
  if (!response?.success && Array.isArray(response?.errors) && response.errors.length > 0) {
    return {
      ...response,
      error: response.errors.map(({ field, message }) => `${field}: ${message}`).join(' • '),
    };
  }

  return response;
};
